// Authentication utility functions

/**
 * Store authentication tokens and user info in localStorage
 * @param {Object} tokens - Object containing accessToken, idToken, refreshToken
 * @param {string} userId - User ID to store
 */
export const storeTokens = (tokens, userId) => {
  if (tokens.accessToken) {
    localStorage.setItem("accessToken", tokens.accessToken);
  }
  if (tokens.idToken) {
    localStorage.setItem("idToken", tokens.idToken);
  }
  if (tokens.refreshToken) {
    localStorage.setItem("refreshToken", tokens.refreshToken);
  }
  // Store userId if provided
  if (userId) {
    localStorage.setItem("userId", userId);
  }
  // Store timestamp for token expiration
  localStorage.setItem("tokenTimestamp", Date.now().toString());
};

/**
 * Store authentication tokens and user info in sessionStorage (for session-only storage)
 * @param {Object} tokens - Object containing accessToken, idToken, refreshToken
 * @param {string} userId - User ID to store
 */
export const storeTokensSession = (tokens, userId) => {
  if (tokens.accessToken) {
    sessionStorage.setItem("accessToken", tokens.accessToken);
  }
  if (tokens.idToken) {
    sessionStorage.setItem("idToken", tokens.idToken);
  }
  if (tokens.refreshToken) {
    sessionStorage.setItem("refreshToken", tokens.refreshToken);
  }
  // Store userId if provided
  if (userId) {
    sessionStorage.setItem("userId", userId);
  }
  sessionStorage.setItem("tokenTimestamp", Date.now().toString());
};

/**
 * Get stored tokens from localStorage
 * @returns {Object} Object containing stored tokens and userId
 */
export const getStoredTokens = () => {
  return {
    accessToken: localStorage.getItem("accessToken"),
    idToken: localStorage.getItem("idToken"),
    refreshToken: localStorage.getItem("refreshToken"),
    userId: localStorage.getItem("userId"),
    timestamp: localStorage.getItem("tokenTimestamp")
  };
};

/**
 * Store user ID in localStorage
 * @param {string} userId - User ID to store
 */
export const storeUserId = (userId) => {
  if (userId) {
    localStorage.setItem("userId", userId);
  }
};

/**
 * Get stored user ID from localStorage
 * @returns {string|null} Stored user ID or null if not found
 */
export const getStoredUserId = () => {
  return localStorage.getItem("userId") || null;
};

/**
 * Get stored tokens from sessionStorage
 * @returns {Object} Object containing stored tokens
 */
export const getStoredTokensSession = () => {
  return {
    accessToken: sessionStorage.getItem("accessToken"),
    idToken: sessionStorage.getItem("idToken"),
    refreshToken: sessionStorage.getItem("refreshToken"),
    timestamp: sessionStorage.getItem("tokenTimestamp")
  };
};

/**
 * Clear all stored tokens
 */
export const clearTokens = () => {
  localStorage.removeItem("accessToken");
  localStorage.removeItem("idToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("tokenTimestamp");
  sessionStorage.removeItem("accessToken");
  sessionStorage.removeItem("idToken");
  sessionStorage.removeItem("refreshToken");
  sessionStorage.removeItem("tokenTimestamp");
};

/**
 * Decode JWT token payload
 * @param {string} token - JWT token
 * @returns {Object|null} Decoded payload or null if invalid
 */
export const decodeJWT = (token) => {
  try {
    if (!token) return null;
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = parts[1];
    const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(decoded);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
};

/**
 * Check if token is expired
 * @param {string} token - JWT token
 * @returns {boolean} True if expired, false if valid
 */
export const isTokenExpired = (token) => {
  const decoded = decodeJWT(token);
  if (!decoded || !decoded.exp) return true;

  const currentTime = Math.floor(Date.now() / 1000);
  return decoded.exp < currentTime;
};

/**
 * Validate stored tokens
 * @param {boolean} useSession - Whether to check sessionStorage instead of localStorage
 * @returns {boolean} True if valid tokens exist, false otherwise
 */
export const validateTokens = (useSession = false) => {
  const tokens = useSession ? getStoredTokensSession() : getStoredTokens();

  // Check if we have an access token
  if (!tokens.accessToken) {
    return false;
  }

  // Check if token is expired
  if (isTokenExpired(tokens.accessToken)) {
    clearTokens();
    return false;
  }

  return true;
};

/**
 * Get user info from ID token
 * @param {boolean} useSession - Whether to check sessionStorage instead of localStorage
 * @returns {Object|null} User info or null if not available
 */
export const getUserInfo = (useSession = false) => {
  const tokens = useSession ? getStoredTokensSession() : getStoredTokens();

  if (!tokens.idToken) {
    return null;
  }

  const decoded = decodeJWT(tokens.idToken);
  if (!decoded) {
    return null;
  }

  return {
    id: decoded.sub,
    email: decoded.email,
    name: decoded.name,
    picture: decoded.picture,
    emailVerified: decoded.email_verified
  };
};

/**
 * Check if user is authenticated
 * @param {boolean} useSession - Whether to check sessionStorage instead of localStorage
 * @returns {boolean} True if authenticated, false otherwise
 */
export const isAuthenticated = (useSession = false) => {
  return validateTokens(useSession);
};